import React from 'react';

import type { AvailableIconName } from '@/assets/icons';
import { Stack } from '@/components/layout';
import { TextInput } from '@/components/ui';
import type { TextInputFieldProps } from '@/components/ui/text-input/types';
import { InputLabel } from '@/features/auth/components/input-label';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

/** Muted slate used for the leading icon / password toggle (matches Figma). */
const FIELD_ICON_COLOR = '#64748B';

type AuthTextFieldProps = {
  label: string;
  required?: boolean;
  /** Leading icon shown inside the field. Omit for a field with no leading icon. */
  iconName?: AvailableIconName;
  value: string;
  onChangeText: (text: string) => void;
  onBlur?: TextInputFieldProps['onBlur'];
  placeholder?: string;
  /**
   * Controlled password visibility. When provided, the field is obscured and a
   * show/hide toggle is rendered. Pair with {@link onSecureTextEntryChange}.
   */
  secureTextEntry?: boolean;
  onSecureTextEntryChange?: (secureTextEntry: boolean) => void;
  /**
   * Validation message. When set, the field shows a red border and renders the
   * message below it.
   */
  error?: string;
  /** Extra props forwarded to the underlying field (keyboardType, autoComplete, etc.). */
  fieldProps?: TextInputFieldProps;
};

/**
 * Labeled text field used across the auth flow: a label above a bordered input
 * with a leading icon and an optional password show/hide toggle.
 */
export function AuthTextField({
  label,
  required,
  iconName,
  value,
  onChangeText,
  onBlur,
  placeholder,
  secureTextEntry,
  onSecureTextEntryChange,
  error,
  fieldProps,
}: AuthTextFieldProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const isPassword = secureTextEntry !== undefined;
  const hasError = !!error;

  return (
    <Stack gap={scale(4)}>
      <InputLabel required={required}>{label}</InputLabel>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        status={hasError ? 'error' : 'default'}
        {...(isPassword ? { secureTextEntry, onSecureTextEntryChange } : null)}>
        {/* Skip the hardcoded border color on error so the red error border shows. */}
        <TextInput.Content style={hasError ? styles.inputContentError : styles.inputContent}>
          {iconName ? (
            <TextInput.Icon
              styleWrapper={styles.inputIcon}
              name={iconName}
              position="left"
              size={scale(20)}
              color={FIELD_ICON_COLOR}
            />
          ) : null}
          <TextInput.Field
            placeholder={placeholder}
            variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
            autoCapitalize="none"
            autoCorrect={false}
            placeholderTextColor={colors['5']}
            onBlur={onBlur}
            {...fieldProps}
          />
          {isPassword ? (
            <TextInput.PasswordToggle
              iconName="IcEye1"
              hiddenIconName="IcEyeSlash"
              size={scale(20)}
              color={FIELD_ICON_COLOR}
            />
          ) : null}
        </TextInput.Content>
        {/*
          Keep the HelperText permanently mounted and only toggle its content/height.
          Previously this was `{hasError ? <HelperText/> : null}`; when a field's error
          cleared then returned (e.g. password: type → clear), React reliably updated the
          error border (driven via context) but intermittently failed to re-mount this
          last toggling child, leaving a red border with no message. Never unmounting it
          removes that null↔element reconciliation entirely.
        */}
        <TextInput.HelperText style={hasError ? undefined : styles.helperTextHidden}>
          {error ?? ''}
        </TextInput.HelperText>
      </TextInput>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  inputContent: {
    height: scale(48),
    borderRadius: scale(8),
    borderColor: colors['3'],
    paddingHorizontal: scale(12),
    paddingLeft: scale(12),
  },
  inputContentError: {
    height: scale(48),
    borderRadius: scale(8),
    borderColor: colors.Error,
    paddingHorizontal: scale(12),
    paddingLeft: scale(12),
  },
  inputIcon: {
    marginRight: scale(8),
  },
  // Collapse the (always-mounted) helper text when there's no error, so it takes no space.
  helperTextHidden: {
    height: 0,
    marginTop: 0,
  },
}));

export default AuthTextField;
